'use client';

// This includes font face declarations and focus-visible polyfill & has side-effects.
// This should go at top of app entry before CDS components.
import '@cbhq/cds-icons/fonts/web/icon-font.css';
import '@cbhq/cds-web/globalStyles';
import '@cbhq/cds-web/defaultFontStyles';

// Coinbase branded fonts (CoinbaseSans, CoinbaseDisplay, CoinbaseMono)
import '@/styles/fonts.css';

import '@/app/globals.css';
// Import OnchainKit CSS after CDS to ensure CDS fonts take precedence
import '@coinbase/onchainkit/onchainkit.css';

import { type ReactNode } from 'react';
import dynamic from 'next/dynamic';
import { CookieBanner } from '@coinbase/cookie-banner';
// DevicePreferencesProvider and FeatureFlagProvider removed in CDS v8
import { ThemeProvider } from '@cbhq/cds-web/system/ThemeProvider';
import { MediaQueryProvider } from '@cbhq/cds-web/system/MediaQueryProvider';
import { PortalProvider } from '@cbhq/cds-web/overlays/PortalProvider';
import { Box } from '@cbhq/cds-web/layout/Box';
import { defaultTheme } from '@cbhq/cds-web/themes/defaultTheme';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import { CookieManagerProvider } from '@/components/CookieManagerProvider/CookieManagerProvider';
import OnchainProviders from '@/onchain/OnchainProviders';
import { Navbar } from '@/components/Navbar';
import { LayoutWrapper } from '@/components/LayoutHelpers/LayoutWrapper';
import { useTheme } from '@/hooks/useTheme';
import { ComplianceProvider } from '@/components/ComplianceProvider/ComplianceProvider';
import { AnalyticsProvider } from '@/components/AnalyticsProvider';
import { PageWrapper } from '@/components/PageWrapper';
import { Footer } from '@/components/Footer';

// Defer Datadog RUM initialization to reduce initial bundle size
const DatadogInit = dynamic(async () => import('@/app/datadog'), {
  ssr: false,
});
import { ChainSwitchProvider } from '@/components/ChainSwitch/ChainSwitchContext';
import { GlobalSettingsProvider } from '@/components/common/Settings/GlobalSettingsProvider';
import { AddLiquidityModalProvider } from '@/components/AddLiquidity/AddLiquidityModalProvider';
import { AddLiquidityModal } from '@/components/AddLiquidity/AddLiquidityModal';
import { DEFAULT_MARGIN, DEFAULT_MARGIN_MOBILE } from '@/constants/layout';

// The AddLiquidityModal and its trigger flows are Verified Pools-specific.
// In the Tokenized Equities app the modal DOM is suppressed; the provider is
// kept so that any call-sites using useAddLiquidityModal() do not throw.
const isVerifiedPoolsApp = process.env.NEXT_PUBLIC_APP_MODE !== 'tokenized-equities';

type Props = {
  children: ReactNode;
};

// Inner component that uses hooks requiring providers
function ClientLayoutInner({ children }: Props) {
  const { theme, toggleTheme } = useTheme();
  const { isPhone, isPhoneLandscape, isTablet } = useBreakpoints();
  const isMobile = isPhone || isPhoneLandscape || isTablet;

  return (
    <ThemeProvider theme={defaultTheme} activeColorScheme={theme}>
      <PortalProvider>
        <ErrorBoundary fill>
          <OnchainProviders>
            <ComplianceProvider>
              <CookieManagerProvider>
                <ChainSwitchProvider>
                  <AnalyticsProvider>
                    <GlobalSettingsProvider>
                      <AddLiquidityModalProvider>
                        <LayoutWrapper>
                          <Navbar theme={theme} toggleTheme={toggleTheme} />
                          <Box
                            alignItems="stretch"
                            flexGrow={1}
                            justifyContent="center"
                            paddingBottom={4}
                            paddingTop={isMobile ? DEFAULT_MARGIN_MOBILE : DEFAULT_MARGIN}
                            background="bgAlternate"
                          >
                            <PageWrapper>{children}</PageWrapper>
                          </Box>
                          <Footer />
                          <CookieBanner companyName="Coinbase" />
                        </LayoutWrapper>
                        {isVerifiedPoolsApp && <AddLiquidityModal />}
                      </AddLiquidityModalProvider>
                    </GlobalSettingsProvider>
                  </AnalyticsProvider>
                </ChainSwitchProvider>
              </CookieManagerProvider>
            </ComplianceProvider>
          </OnchainProviders>
        </ErrorBoundary>
      </PortalProvider>
    </ThemeProvider>
  );
}

export default function ClientLayout({ children }: Props) {
  return (
    <body>
      <DatadogInit />
      <MediaQueryProvider>
        <ClientLayoutInner>{children}</ClientLayoutInner>
      </MediaQueryProvider>
    </body>
  );
}
